
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import PendingApprovalsTable from '../PendingApprovalsTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { syncReferrerNames } from '@/utils/referralUtils';

interface Member {
  id: string;
  full_name: string;
  phone_number: string;
  state: string;
  local_government: string;
  created_at: string;
  email?: string;
  referred_by?: string | null;
  referrer_name?: string | null;
}

const PendingMembersSection = () => {
  const [pendingMembers, setPendingMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPendingMembers = useCallback(async () => {
    setLoading(true);
    try {
      console.log('[PendingMembers] Fetching pending members...');
      
      const { data, error } = await supabase
        .from('community_members')
        .select('id, full_name, phone_number, state, local_government, created_at, email, referred_by, referrer_name')
        .eq('approved', false)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[PendingMembers] Error fetching pending members:', error);
        throw error;
      }
      
      console.log('[PendingMembers] Successfully fetched pending members:', data?.length || 0);
      setPendingMembers(data || []);
    } catch (error) {
      console.error('[PendingMembers] Error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load pending members."
      });
      setPendingMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingMembers();
  }, [fetchPendingMembers]);

  const approveMember = async (memberId: string) => {
    try {
      console.log("[PendingMembers] Approving member:", memberId);

      const { error: updateError } = await supabase
        .from('community_members')
        .update({ approved: true })
        .eq('id', memberId);

      if (updateError) {
        console.error('[PendingMembers] Error updating member status:', updateError);
        toast({
          variant: "destructive", 
          title: "Error",
          description: `Failed to approve member: ${updateError.message}`
        });
        return;
      }

      toast({
        title: "Success",
        description: "Member approved successfully!"
      });
      
      fetchPendingMembers();

      // Background sync of referrer names
      setTimeout(async () => {
        try {
          await syncReferrerNames(); 
          console.log("[PendingMembers] syncReferrerNames completed after approval.");
        } catch (error) {
          console.error("[PendingMembers] Error syncing referrer names post-approval:", error);
        }
      }, 100);

    } catch (error) {
      console.error('[PendingMembers] Unexpected error approving member:', error);
      toast({
        variant: "destructive",
        title: "Error", 
        description: "An unexpected error occurred while approving member. Please try again."
      });
    }
  };

  const rejectMember = async (memberId: string) => {
    try {
      console.log("[PendingMembers] Rejecting member:", memberId);
      
      const { error } = await supabase
        .from('community_members')
        .delete()
        .eq('id', memberId);

      if (error) {
        console.error('[PendingMembers] Error rejecting member:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to reject member: ${error.message}`
        });
        return;
      }
      
      toast({
        title: "Success",
        description: "Member rejected and removed."
      });
      fetchPendingMembers();
    } catch (error) {
      console.error('[PendingMembers] Unexpected error rejecting member:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred while rejecting member. Please try again."
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Member Approvals</CardTitle>
      </CardHeader>
      <CardContent>
        <PendingApprovalsTable
          pendingMembers={pendingMembers}
          onApprove={approveMember}
          onReject={rejectMember}
          loading={loading}
        />
      </CardContent>
    </Card>
  );
};

export default PendingMembersSection;
